import { memo, useCallback, useMemo } from "react";
import { parseISO } from "date-fns";
import { PropertyDataV2 } from "commonModule/services/common";
import PropertyImageSlider from "components/slider/PropertyImageSlider";
import TravelAgentPropertyCardBody from "components/travel-agent/TravelAgentPropertyCardBody";
import useCurrency from "hooks/swr/useCurrency";
import { useAppSelector } from "stores/hooks";
import { selectSearch, selectSearchQuery } from "stores/search/searchSlice";
import { diffDays } from "utils/dates";
import { getFiltersForMixpanelEvent, getSavedPrice } from "utils/helper";
import { setDetailPageOrigin, setLastSearchUrl, setPropertyPriceForDetailPageEvent } from "utils/local-storage";
import { buildDetailPageUrl } from "utils/url";
const SingleUnitContent = memo(({
  property,
  shouldDisplayTotalPrice,
  propertyIndex,
  isPriceMarker,
  pageOrigin,
  hasMultiple = true,
  setIsCloseOnOutsideEnabled
}: {
  property: PropertyDataV2;
  shouldDisplayTotalPrice: boolean;
  propertyIndex?: number;
  isPriceMarker?: boolean;
  pageOrigin: string;
  hasMultiple?: boolean;
  isFavourite?: boolean;
  setIsCloseOnOutsideEnabled?: (value: boolean) => void;
}) => {
  const search = useAppSelector(selectSearch);
  const searchQuery = useAppSelector(selectSearchQuery);
  const {
    rate,
    formatter
  } = useCurrency();
  const isMultiUnitProperty = property?.multiUnitGrp && property?.multiUnitCount && property?.multiUnitCount > 1 && hasMultiple;
  const numericSavePrice = getSavedPrice({
    shouldDisplayTotalPrice,
    searchQuery,
    property,
    rate,
    formatter,
    addCurrencySymbol: false
  });
  const propertyUrl = useMemo(() => buildDetailPageUrl({
    name: search?.shortName || " ",
    propertyId: property._id,
    propertyName: property?.propertySubTypeDesc || property.propertyName,
    adultCount: search?.adultCount,
    childrenCount: search?.childrenCount,
    petAllowed: search?.petAllowed,
    startDate: search?.startDate || property.checkInDate,
    endDate: search?.endDate || property.checkOutDate,
    eventAllowed: search?.eventAllowed,
    smokeAllowed: search?.smokeAllowed,
    childAllowed: search?.childAllowed,
    FeaturesAndAmenities: search?.FeaturesAndAmenities?.join(",") || "",
    sortBy: search?.sortBy || "highToLow",
    minBedRoom: search?.numberOfBedroom?.toString(),
    minBed: search?.minBed?.toString(),
    minBathRoom: search?.numberOfBathroom?.toString(),
    minPrice: (search?.minPrice ?? 0).toString(),
    maxPrice: (search?.maxprice ?? 0).toString(),
    cancellable: search?.cancellable,
    searchPrice: property?.totalBeforeTaxes ? Math.round(Number(property.totalBeforeTaxes)).toString() : "",
    savedPrice: numericSavePrice || "",
    strikePrice: (property?.originalTotalBeforeTaxes * rate)?.toFixed(2) || "",
    source: "search"
  }), [property._id, property.checkInDate, property.checkOutDate, property?.propertySubTypeDesc, property.propertyName, search?.shortName, search?.adultCount, search?.childrenCount, search?.petAllowed, search?.startDate, search?.endDate, search?.eventAllowed, search?.smokeAllowed, search?.childAllowed, search?.FeaturesAndAmenities, search?.sortBy, search?.numberOfBedroom, search?.minBed, search?.numberOfBathroom, search?.minPrice, search?.maxprice, search?.cancellable, property?.totalBeforeTaxes, numericSavePrice, property?.originalTotalBeforeTaxes, rate]);
  const track = () => {
    setLastSearchUrl(window.location.pathname + window.location.search);
    setPropertyPriceForDetailPageEvent(property?.totalBeforeTaxes ? property.totalBeforeTaxes.toString() : "NA");
    if (propertyIndex && isPriceMarker) {
      void (async () => {
        const selectedFilters = getFiltersForMixpanelEvent(searchQuery);
        const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
        mixpanelV2Client?.CLICK_PROPERTY_ON_SEARCH_RESULT_PAGE({
          ranking: propertyIndex + 1 + (searchQuery?.page || 0) * 21,
          actualPrice: Math.round(property.originalTotalBeforeTaxes),
          city: property?.city,
          state: property?.state,
          country: property?.country,
          dates: searchQuery?.startDate || searchQuery?.endDate ? `${searchQuery?.startDate || ""}, ${searchQuery?.endDate || ""}` : "NA",
          filters: selectedFilters.join(",") || "NA",
          maxPrice: searchQuery?.maxPrice == 1001 ? "NA" : searchQuery?.maxPrice || "NA",
          methodOfSearch: "map area",
          minPrice: searchQuery?.minPrice || "NA",
          noOfGuest: (searchQuery?.adultCount || 0) + (searchQuery?.childrenCount || 0) || "NA",
          numberOfBedroom: searchQuery?.numberOfBedroom || "NA",
          numberOfBeds: searchQuery?.minBed || "NA",
          numberOfBathroom: searchQuery?.numberOfBathroom || "NA",
          propertyId: property._id,
          discountPrice: Math.round(property.totalBeforeTaxes),
          propertyRating: property?.propertyStarSum || "NA",
          savedPrice: Math.round(property.discount),
          shouldDisplayTotalPrice: !!searchQuery?.toggleOn,
          sortBy: searchQuery?.sortBy || "highToLow",
          totalNights: searchQuery?.startDate && searchQuery?.endDate && searchQuery?.noOfDays ? searchQuery?.noOfDays : "NA",
          numberOfDaysBeforeCheckin: searchQuery?.startDate ? diffDays(parseISO(new Date().toJSON().slice(0, 10)), new Date(searchQuery?.startDate)) : 0
        });
      })();
    }
    if (isMultiUnitProperty) {
      const location = [property.city, property.postalName, property.state]?.filter(x => !!x)?.join(", ") || "";
      void (async () => {
        const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
        mixpanelV2Client?.CLICK_MAP_CARD({
          propertyTitle: property?.propertyName || "",
          location,
          noOfUnits: property?.multiUnitCount || 0,
          displayedPrice: property?.totalBeforeTaxes ? `${formatter(0, 0).format(property.totalBeforeTaxes * rate)}` : "NA"
        });
      })();
    }
    setDetailPageOrigin(pageOrigin);
  };
  const trackImageSlideEvent = useCallback((index: number) => {
    void (async () => {
      const mixpanelV1Client = (await import("services/mixpanel")).mixpanelV1Client;
      mixpanelV1Client.INTERESTED_IN_PROPERTY_PICTURES({
        index,
        propertyId: property._id
      });
    })();
  }, [property._id]);
  return <>
        <div className='map-card-image-slider h-[240px] w-full overflow-hidden rounded-lg'>
          <PropertyImageSlider propertyId={property._id} images={property.images.slice(0, 5)} afterChange={trackImageSlideEvent} propertyUrl={propertyUrl} onClick={track} defaultAlt={`${property.propertyName} in ${property.city}, ${property.state}`} className='!h-[240px]' isDragOnDesktop={false} />
        </div>
        <div className='mt-[15px] lg:mt-2.5'>
          <TravelAgentPropertyCardBody property={property} propertyUrl={propertyUrl} onLinkClick={track} setIsCloseOnOutsideEnabled={setIsCloseOnOutsideEnabled} />
        </div>
      </>;
});
SingleUnitContent.displayName = "SingleUnitContent";
export default SingleUnitContent;